<template>
  <v-app-bar color="secondary" density="compact" :flat="true">
    <v-toolbar-title class="text-button">{{ icdPresentationString }}: {{ icdDescription }} - {{ selected_menu_item.title }}</v-toolbar-title>
    <v-btn variant="text" icon="mdi-information"
           @click.stop="information_drawer = !information_drawer"
           :disabled="dashboard_6.visible"></v-btn>

    <template v-slot:extension>
      <v-tabs v-model="selected_menu_item" align-tabs="title" show-arrows>
        <v-tab v-for="menu_item in menu_items" :key="menu_item.dashboard" :value="menu_item"
               :disabled="menu_item.accessRight && !isAuthorized(menu_item.accessRight)">
          {{ menu_item.title }}
        </v-tab>
      </v-tabs>
    </template>
  </v-app-bar>

  <v-window v-model="selected_menu_item" direction="horizontal">
    <v-window-item :key="dashboard_1.dashboard" :value="dashboard_1" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_1.visible = event.value">
      <RareDiseaseDemographicBaseline
          :icd="icd"
          :icd-description="icdDescription"
          :visible="this.dashboard_1.visible"
          v-if="!this.initializing">
      </RareDiseaseDemographicBaseline>
    </v-window-item>
    <v-window-item :key="dashboard_2.dashboard" :value="dashboard_2" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_2.visible = event.value">
      <RareDiseaseTreatmentPatternInterventions
          :icd="icd"
          :visible="this.dashboard_2.visible"
          v-if="!this.initializing">
      </RareDiseaseTreatmentPatternInterventions>
    </v-window-item>
    <v-window-item :key="dashboard_3.dashboard" :value="dashboard_3" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_3.visible = event.value">
      <RareDiseaseTreatmentPatternPrescribedDrugs
          :icd="icd"
          :visible="this.dashboard_3.visible"
          v-if="!this.initializing">
      </RareDiseaseTreatmentPatternPrescribedDrugs>
    </v-window-item>
    <v-window-item :key="dashboard_4.dashboard" :value="dashboard_4" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_4.visible = event.value">
      <RareDiseaseComorbidity
          :icd="icd"
          :icd-description="icdDescription"
          :visible="this.dashboard_4.visible"
          v-if="!this.initializing">
      </RareDiseaseComorbidity>
    </v-window-item>
    <v-window-item :key="dashboard_5.dashboard" :value="dashboard_5" transition="fade-transition"
                   reverse-transition="fade-transition" @group:selected="(event) => this.dashboard_5.visible = event.value">
      <RareDiseaseSpecificQuestions
          :icd="icd"
          :icd-description="icdDescription"
          :visible="this.dashboard_5.visible"
          v-if="!this.initializing">
      </RareDiseaseSpecificQuestions>
    </v-window-item>
    <v-window-item :key="dashboard_6.dashboard" :value="dashboard_6" transition="fade-transition"
                   reverse-transition="fade-transition"
                   @group:selected="(event) => {
                     this.dashboard_6.visible = event.value;
                     this.information_drawer &&= !event.value;
                   }">
      <ClinicalTrials :mesh-ids="meshIds"
                      :mesh-description="meshDescription"
                      :visible="this.dashboard_6.visible" v-if="!this.initializing"></ClinicalTrials>
    </v-window-item>
  </v-window>

  <v-navigation-drawer v-model="information_drawer" v-bind:width="500" location="right" temporary>
    <v-list density="compact" nav>
      <v-list-item prepend-icon="mdi-information" title="Information about the data set" nav></v-list-item>
    </v-list>

    <v-divider></v-divider>

    <v-list density="compact" nav>
      <v-list-item><strong>Population definition</strong></v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        Individuals with {{ diseaseDescription }} (ICD10: {{ icdPresentationString }}) have been identified in the
        Patient Register from January, 2000 until May, 2024.
      </v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        Date of diagnosis in the Patient Register has been used as index date. The diagnosis date is defined as the
        date of Fabry disease first recorded for the patient in the Patient Register.
      </v-list-item>
      <v-list-item prepend-icon="mdi:mdi-minus">
        From the Patient Register, the Prescribed Drug Register, and the Cause-of-Death Register, selected
        information occurring from (and including) the index date has been retrieved. Selected information
        occurring before the index date has also been retrieved.
      </v-list-item>

    </v-list>
  </v-navigation-drawer>

</template>

<script>
import {defineComponent} from "vue";
import RareDiseaseDemographicBaseline from '@/components/RareDiseaseDemographicBaseline.vue'
import RareDiseaseTreatmentPatternInterventions from '@/components/RareDiseaseTreatmentPatternInterventions.vue'
import RareDiseaseTreatmentPatternPrescribedDrugs from '@/components/RareDiseaseTreatmentPatternPrescribedDrugs.vue'
import RareDiseaseComorbidity from "@/components/RareDiseaseComorbidity.vue";
import RareDiseaseSpecificQuestions from "@/components/RareDiseaseSpecificQuestions.vue";
import ClinicalTrials from "@/components/ClinicalTrials.vue";

const DASHBOARD_1 = {
  title: 'Epidemiology and baseline characteristics',
  dashboard: 'Dashboard1',
  visible: true
};

const DASHBOARD_2 = {
  title: 'Treatment pattern, interventions given in hospital',
  dashboard: 'Dashboard2',
  visible: false
};

const DASHBOARD_3 = {
  title: 'Treatment pattern, prescribed drugs',
  dashboard: 'Dashboard3',
  visible: false
};

const DASHBOARD_4 = {
  title: 'Comorbidity',
  dashboard: 'Dashboard4',
  visible: false
};

const DASHBOARD_5 = {
  title: 'Specific questions',
  dashboard: 'Dashboard5',
  visible: false
};

const DASHBOARD_6 = {
  title: 'Clinical trials',
  dashboard: 'Dashboard6',
  accessRight: 'clinical-trials',
  visible: false
};

export default defineComponent({
  name: 'RareDiseaseView',
  props: {
    icd: String,
    icdPresentationString: String,
    icdDescription: String,
    diseaseDescription: String,
    meshIds: Array,
    meshDescription: String
  },
  components: {
    RareDiseaseDemographicBaseline,
    RareDiseaseTreatmentPatternInterventions,
    RareDiseaseTreatmentPatternPrescribedDrugs,
    RareDiseaseComorbidity,
    RareDiseaseSpecificQuestions,
    ClinicalTrials
  },
  computed: {
    selectedSurvivalTimeInYears() {
      return this.selected_survival_time_in_years.map(f => f.value)
    }
  },
  data: () => ({
    initializing: true,
    dashboard_1: DASHBOARD_1,
    dashboard_2: DASHBOARD_2,
    dashboard_3: DASHBOARD_3,
    dashboard_4: DASHBOARD_4,
    dashboard_5: DASHBOARD_5,
    dashboard_6: DASHBOARD_6,
    selected_menu_item: DASHBOARD_1,
    information_drawer: false,
    menu_items: [
      DASHBOARD_1,
      DASHBOARD_2,
      DASHBOARD_3,
      DASHBOARD_4,
      DASHBOARD_5,
      DASHBOARD_6
    ]
  }),
  methods: {
    isAuthorized(accessRight) {
      return this.$store.getters.stateUser
          && this.$store.getters.stateUser.access_rights
          && this.$store.getters.stateUser.access_rights.includes(accessRight)
    },
    async loadStaticData() {
    },
    async loadDynamicData() {
    }
  },
  watch: {
    icd() {
      this.loadStaticData().then(() => {
        this.loadDynamicData()
      })
    }
  },
  async mounted() {
    this.initializing = true
    await this.loadStaticData().then(() => {
      this.initializing = false
      return this.loadDynamicData()
    }).finally(() => {
      this.initializing = false
    })
  }
})
</script>
